/**
 * Neutral colours for figures that are drawn rather than styled.
 *
 * CSS can be given a second palette; a canvas cannot. Every instrument in this
 * platform paints its labels, axes, tracks and housings with literal colours
 * chosen for a dark room, so in the daylight view the figure itself changes but
 * the writing on it does not — captions sit at near-white on white paper.
 *
 * `N()` maps one neutral to its daylight counterpart. It is a lookup, not a
 * calculation: a lightness flip would drag the hues with it, and these values
 * were chosen against each other.
 *
 * WHAT IS NOT IN HERE, DELIBERATELY: the state colours. Green, amber and red
 * mean within, watch and outside limits, and an operator learns those. They are
 * the same in both views, which is the same rule the stylesheet follows.
 *
 * The dark view is the platform's normal one; there `N()` returns its argument
 * untouched except for the caption greys, which are raised for contrast.
 */

/**
 * Dark-room value → daylight value.
 *
 * The ramp is read top to bottom as: text, then quieter text, then lines, then
 * the recessed surfaces an instrument sits in. Light values are darkened rather
 * than inverted so each one still clears 4.5:1 against paper.
 */
const DAYLIGHT: Readonly<Record<string, string>> = Object.freeze({
  /* text, brightest first */
  "#E9F0F5": "#121D27",
  "#DDE8EF": "#1B2A36",
  "#C4D3DE": "#2A3B49",
  "#9DB2C0": "#3C4E5C",
  "#93A9B8": "#445765",
  "#8CA3B3": "#48596A",
  "#7E93A3": "#526574",
  "#6E8496": "#5E7284",
  /*
   * #5A7285 is deliberately absent. It is the IDLE state colour — "no data" —
   * as well as a neutral grey, and the rule above says state colours are not
   * mapped. It reads as mid-grey on paper either way, so leaving it alone costs
   * nothing and keeps the rule true without an exception.
   */
  /* the quietest label in the ramp — on paper it has to come up a long way or
     it is not a label, it is a smudge */
  "#3A4C58": "#6B7E8D",
  /* rules and gridlines */
  "#2F3E49": "#C2D0DB",
  "#2B3A46": "#CBD8E2",
  "#25333E": "#D2DEE7",
  "#22303B": "#D8E2EA",
  "#1F2C36": "#DCE4EC",
  /* recessed housings and tracks: the darkest surfaces become the lightest */
  "#1B2731": "#DCE4EC",
  "#16222C": "#E3EAF1",
  "#15202A": "#E3EAF1",
  "#111C24": "#EAF0F5",
  "#101B24": "#EAF0F5",
  "#0B1620": "#F2F6FA",
  "#0B141C": "#F5F8FB",
  "#0A131B": "#F5F8FB",
  "#08131B": "#F7FAFC",
  "#06121A": "#F7FAFC",
});

/**
 * Dark-room text greys → brighter ones (2026-09-27).
 *
 * The captions on the figures were drawn in greys picked to sit back, and on
 * the black screen they sat back too far: #3A4C58, "the quietest label", is
 * about 2:1 on the housings — not a label, a smudge. Raised so every caption
 * clears 5:1 and the ramp keeps its order.
 */
const DARK_TEXT: Readonly<Record<string, string>> = Object.freeze({
  "#9DB2C0": "#C3D1DB",
  "#93A9B8": "#BCCBD6",
  "#8CA3B3": "#B6C6D2",
  "#7E93A3": "#ABBDCA",
  "#6E8496": "#A2B5C3",
  "#3A4C58": "#8DA3B3",
});

/** True while the daylight view is on; the stylesheet keys on the same class. */
const daylight = () =>
  typeof document !== "undefined" && document.body.classList.contains("lightview");

/**
 * The daylight counterpart of a neutral, or the value unchanged.
 *
 * Anything not in the table — every state colour, every brand colour — comes
 * back exactly as given, so passing a colour through this is always safe.
 */
export function N(hex: string): string {
  if (!daylight()) return DARK_TEXT[hex.toUpperCase()] ?? hex;
  return DAYLIGHT[hex.toUpperCase()] ?? hex;
}

/** For assertions and for anyone checking the ramp is complete. */
export const NEUTRALS = Object.freeze(Object.keys(DAYLIGHT));
